/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  PortableText,
  PortableTextBlock,
  PortableTextMarkComponentProps,
  PortableTextReactComponents,
  PortableTextTypeComponentProps,
} from '@portabletext/react';
import React from 'react';
import { useCookieSettings } from '../contexts/CookieSettingsContext';
import { PageReferenceType } from '../graphql-fragments/ButtonLink';
import { RawPortableText } from '../types/types';
import { getReferenceUrl, SANITY_TYPE_TO_TYPENAME, slugify } from '../utils/utils';
import * as styles from './portableText.module.scss';
import TextLink from './ui/TextLink';

const IMAGE_WIDTHS = [600, 900, 1200, 1600];
// Above this width images sit in the right column, which is half the viewport ($layout-phablet).
const IMAGE_HALF_WIDTH_BREAKPOINT = '767px';

function getIdFromValue(value: PortableTextBlock): string | undefined {
  return slugify(
    value.children.map(child => (typeof child.text === 'string' ? child.text : '')).join(''),
  );
}

export const components = {
  block: {
    normal: ({ children }) => <p>{children}</p>,
    p: ({ children }) => <p>{children}</p>,
    h2: ({ value, children }) => <h2 id={getIdFromValue(value)}>{children}</h2>,
    h3: ({ value, children }) => <h3 id={getIdFromValue(value)}>{children}</h3>,
    h4: ({ value, children }) => <h4 id={getIdFromValue(value)}>{children}</h4>,
    h5: ({ value, children }) => <h5 id={getIdFromValue(value)}>{children}</h5>,
  },
  marks: {
    internalLink: (
      props: PortableTextMarkComponentProps<{
        _type: 'internalLink';
        linkToCookieSettings?: boolean;
        reference?: {
          slug: { current: string };
          _type: PageReferenceType;
        };
        anchorLink?: string;
      }>,
    ) => {
      const { showCookieSettingsModal } = useCookieSettings();

      if (props.value!.linkToCookieSettings) {
        return (
          <a
            className={styles.link}
            onClick={() => {
              showCookieSettingsModal();
            }}
          >
            {props.children}
          </a>
        );
      } else {
        return (
          <TextLink
            to={
              (props.value!.reference
                ? getReferenceUrl(
                    SANITY_TYPE_TO_TYPENAME[props.value!.reference._type],
                    props.value!.reference.slug.current,
                  )
                : '') +
              (props.value!.anchorLink ? '#' + props.value!.anchorLink.replace('#', '') : '')
            }
          >
            {props.children}
          </TextLink>
        );
      }
    },

    externalLink: (
      props: PortableTextMarkComponentProps<{
        _type: 'externalLink';
        url: string;
      }>,
    ) => {
      return (
        <TextLink openInNewTab to={props.value!.url}>
          {props.children}
        </TextLink>
      );
    },
  },
  types: {
    image: (
      props: PortableTextTypeComponentProps<{
        asset: {
          url: string,
          altText?: string,
          metadata?: { dimensions?: { width: number, height: number } },
        };
        alt?: string;
        caption?: string;
      }>,
    ) => {
      const asset = props.value?.asset;
      if (!asset) {
        return null;
      }

      const dimensions = asset.metadata?.dimensions;
      const src = (width: number) => `${asset.url}?w=${width}&auto=format&q=80`;

      return (
        <figure className={styles.imageFigure}>
          <img
            src={src(1200)}
            srcSet={IMAGE_WIDTHS.map(width => `${src(width)} ${width}w`).join(', ')}
            sizes={`(min-width: ${IMAGE_HALF_WIDTH_BREAKPOINT}) 50vw, 100vw`}
            width={dimensions?.width}
            height={dimensions?.height}
            alt={props.value.alt || asset.altText || ''}
            loading="lazy"
            decoding="async"
          />
          {props.value.caption && (
            <figcaption className={styles.caption}>{props.value.caption}</figcaption>
          )}
        </figure>
      );
    },

    quote: (
      props: PortableTextTypeComponentProps<{
        quote: string;
        text: RawPortableText;
      }>,
    ) => {
      return (
        <div className={styles.quoteContainer}>
          <blockquote className={styles.quote}>{props.value.quote}</blockquote>
          <div className={styles.text}>
            <PortableText value={props.value.text} components={components} />
          </div>
        </div>
      );
    },
  },
  list: {
    number: ({ children, value }) =>
      value.level === 2 ? <ol type="a">{children}</ol> : <ol>{children}</ol>,
  },
} satisfies Partial<PortableTextReactComponents>;
